import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Package,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Search,
  AlertCircle,
  LayoutGrid,
  Columns,
  Table as TableIcon,
  X,
  FileText,
  ClipboardList,
  Speech,
} from "lucide-react";
import "./ProdManager.scss";
import "../../components/WarningPopup/WarningPopup.scss";

import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import WebSocketService from "../../components/WebSockets/WebSocketService";
import { useAuth } from "../../contexts/AuthContext";
import MinutesOfMeetingManager from "../../components/EnquiryStageHistory/MinutesOfMeetingManager";
import EnquiryBOMModal from "../../components/EnquiryStageHistory/EnquiryBOMModal";
import { Icon } from "@iconify/react";

// Kanban ("By Stage") columns now come from a lightweight, paginated
// endpoint (`/operation-categories/team/operation-stage-histories`) that
// returns a flat array of card summaries for a single stage at a time,
// instead of the old bulk `/operation-stage-histories/{categoryId}` shape.
// The API takes `page` (0-based on first load, then 1, 2, 3... as the user
// scrolls) and `size` instead of fromRecord/toRecord. Each column tracks
// its own nextPage/hasMore and fetches its next page as the user scrolls
// near the bottom (mirrors Enquiries.jsx).
const STAGE_HISTORY_PAGE_SIZE = 10;

const ProdManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  // ADD: Connection status for the "Live Updates" header pill (swaps out
  // the manual Refresh button when the team-operations socket is up).
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [operationCategories, setOperationCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [allOperations, setAllOperations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [processingAction, setProcessingAction] = useState(null);
  const [employeesCache, setEmployeesCache] = useState({});
  const [stageOperations, setStageOperations] = useState({});
  const [paginationByStage, setPaginationByStage] = useState({});
  const loadingStageIdsRef = useRef(new Set());

  // ADD: remembers each "By Stage" kanban column's scroll position so it
  // doesn't jump back to the top on re-render (e.g. expanding a card,
  // fetching its checklist, or a WebSocket-triggered refresh). columnNodesRef
  // maps stageId -> the scrollable DOM node; columnScrollPositionRef maps
  // stageId -> the last scrollTop we saw. A layout effect re-applies the
  // remembered position after every render, before the browser paints.
  const columnNodesRef = useRef({});
  const columnScrollPositionRef = useRef({});
  const [viewMode, setViewMode] = useState("stage");
  const [draggedItem, setDraggedItem] = useState(null);
  const [detailedCategoriesCache, setDetailedCategoriesCache] = useState({});
  const [expandedTableRows, setExpandedTableRows] = useState(new Set());
  const [returnPopup, setReturnPopup] = useState({
    isOpen: false,
    stageHistoryId: null,
  });
  const [returnRemarks, setReturnRemarks] = useState("");
  const [completedOrders, setCompletedOrders] = useState([]);
  const paginationRef = useRef({});

  // ADD: card ids currently fetching their full checklist detail (see
  // fetchCardChecklistDetail below). The "By Stage" kanban cards come from
  // the lightweight paginated endpoint, which only has task counts, so we
  // backfill operationStageCheckListHistories/customSpecificationHistories
  // on demand the first time a card is expanded.
  const [loadingCardDetail, setLoadingCardDetail] = useState(new Set());

  // ADD: historyId -> { operationStageCheckListHistories, ... } fetched by
  // fetchCardChecklistDetail. Kept separate from `stageOperations` (rather
  // than merged into it directly) because `stageOperations` gets fully
  // reset back to lightweight/no-checklist data whenever a column reloads
  // (e.g. WebSocket-triggered refreshData(), pagination "reset"). This
  // cache survives those resets and is re-merged into the lightweight
  // records every time they're recomputed (see organizedByStages).
  const [checklistDetailCache, setChecklistDetailCache] = useState({});

  // ADD: Per-category/per-stage operation counts, from the dedicated
  // operation-status-summary endpoint, used for the "column-count" badges
  // in both the By Category and By Stage kanban views instead of counting
  // whatever happens to be loaded client-side.
  const [statusSummary, setStatusSummary] = useState([]);

  // MOM and BOM Modal States
  const [momModal, setMomModal] = useState({
    isOpen: false,
    enquiryId: null,
    enquiryStageHistoryId: null,
    readOnly: false,
    productName: "",
  });

  const [bomModal, setBomModal] = useState({
    isOpen: false,
    enquiryData: null,
  });

  useEffect(() => {
    fetchOperationCategories();
    fetchOperationStatusSummary();
  }, []);

  const fetchAllCategoriesData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = operationCategories.map((category) =>
        axiosInstance.get(
          `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage-histories/${category.id}`,
        ),
      );

      const responses = await Promise.all(promises);

      const allData = {};
      responses.forEach((response, index) => {
        if (response.data.status === 200) {
          allData[operationCategories[index].id] = response.data.data || [];
        }
      });

      setAllOperations(allData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [operationCategories]);

  const fetchCompletedOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_PROD_MANAGER}/product-order-history/completed`,
      );

      if (response.data.status === 200) {
        setCompletedOrders(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch completed orders:", error);
      toast.error("Failed to load completed orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    paginationRef.current = paginationByStage;
  }, [paginationByStage]);

  // Fetches one page (10 records) of lightweight card summaries for a
  // single stage from `/operation-categories/team/operation-stage-histories`.
  // Called once per stage with `reset: true` when a category is opened, and
  // again (without `reset`) whenever that stage's column is scrolled within
  // ~150px of the bottom and it still has more pages.
  const fetchStageHistoryPage = useCallback(
    async (stageId, { reset = false } = {}) => {
      if (loadingStageIdsRef.current.has(stageId)) return;

      const existingPagination = paginationRef.current[stageId];
      if (!reset && existingPagination && !existingPagination.hasMore) return;

      // First load for a stage uses page 0; subsequent scroll-triggered
      // loads use the incrementing page number (1, 2, 3...) we stored from
      // the previous response.
      const page = reset ? 0 : (existingPagination?.nextPage ?? 1);

      loadingStageIdsRef.current.add(stageId);

      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_PROD_MANAGER}/operation-categories/team/operation-stage-histories`,
          { params: { stageId, page, size: STAGE_HISTORY_PAGE_SIZE } },
        );

        if (response.data.status === 200) {
          const records = response.data.data || [];

          setStageOperations((prev) => ({
            ...prev,
            [stageId]: reset ? records : [...(prev[stageId] || []), ...records],
          }));

          setPaginationByStage((prev) => ({
            ...prev,
            [stageId]: {
              nextPage: page + 1,
              hasMore: records.length === STAGE_HISTORY_PAGE_SIZE,
            },
          }));
        }
      } catch (error) {
        console.error("Failed to fetch operation stage history page:", error);
      } finally {
        loadingStageIdsRef.current.delete(stageId);
      }
    },
    [],
  );

  // Triggered when a category "card" is opened (selected) in the By Stage
  // view. Fetches the full category detail — `/operation-categories/{categoryId}` —
  // to get the ordered stage list (with checklists), then kicks off the
  // initial page (records 0-10) for every stage column in parallel.
  const fetchDetailedCategory = useCallback(
    async (categoryId, { force = false } = {}) => {
      if (!force && detailedCategoriesCache[categoryId]) {
        return detailedCategoriesCache[categoryId];
      }

      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_PROD_MANAGER}/operation-categories/${categoryId}`,
        );

        if (response.data.status === 200) {
          const detailedData = response.data.data;
          setDetailedCategoriesCache((prev) => ({
            ...prev,
            [categoryId]: detailedData,
          }));
          return detailedData;
        }
        return null;
      } catch (error) {
        console.error("Failed to fetch detailed category:", error);
        return null;
      }
    },
    [detailedCategoriesCache],
  );

  const loadStageViewData = useCallback(
    async (categoryId, { force = false } = {}) => {
      setLoading(true);
      try {
        const detailedCategory = await fetchDetailedCategory(categoryId, {
          force,
        });
        if (!detailedCategory?.operationStages) return;

        await Promise.all(
          detailedCategory.operationStages.map((stage) =>
            fetchStageHistoryPage(stage.id, { reset: true }),
          ),
        );
      } catch (error) {
        console.error("Failed to load stage view data:", error);
      } finally {
        setLoading(false);
      }
    },
    [fetchDetailedCategory, fetchStageHistoryPage],
  );

  useEffect(() => {
    if (viewMode === "stage" && selectedCategory) {
      loadStageViewData(selectedCategory.id);
    } else if (viewMode === "category" || viewMode === "table") {
      fetchAllCategoriesData();
    } else if (viewMode === "completed") {
      fetchCompletedOrders();
    }
  }, [
    viewMode,
    selectedCategory,
    fetchAllCategoriesData,
    loadStageViewData,
    fetchCompletedOrders,
  ]);

  const fetchOperationCategories = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_PROD_MANAGER}/operation-categories`,
      );

      if (response.data.status === 200) {
        setOperationCategories(response.data.data || []);
        if (response.data.data?.length > 0) {
          setSelectedCategory(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ADD: Fetch per-category/per-stage operation counts from
  // /operation-categories/operation-status-summary (no params — returns
  // every category/stage's totalOperations in one shot).
  const fetchOperationStatusSummary = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_PROD_MANAGER}/operation-categories/operation-status-summary`,
      );

      if (response.data.status === 200) {
        setStatusSummary(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch operation status summary:", error);
    }
  };

  // ADD: categoryId -> total operations, and stageId -> total operations,
  // derived from statusSummary. Category total is the sum of its stages'
  // totalOperations (the endpoint doesn't return a category-level total).
  const stageCountMap = useMemo(() => {
    const map = {};
    statusSummary.forEach((category) => {
      (category.stages || []).forEach((stage) => {
        map[stage.stageId] = stage.totalOperations || 0;
      });
    });
    return map;
  }, [statusSummary]);

  const categoryCountMap = useMemo(() => {
    const map = {};
    statusSummary.forEach((category) => {
      map[category.categoryId] = (category.stages || []).reduce(
        (sum, stage) => sum + (stage.totalOperations || 0),
        0,
      );
    });
    return map;
  }, [statusSummary]);

  const fetchEmployeesForStage = useCallback(
    async (operationStageId) => {
      if (employeesCache[operationStageId]) {
        return employeesCache[operationStageId];
      }

      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/operation-stage/${operationStageId}`,
        );

        if (response.data.status === 200) {
          const employees = response.data.data || [];
          setEmployeesCache((prev) => ({
            ...prev,
            [operationStageId]: employees,
          }));
          return employees;
        }

        return [];
      } catch (error) {
        console.error("Failed to fetch employees:", error);
        return [];
      }
    },
    [employeesCache],
  );

  const refreshData = useCallback(() => {
    fetchOperationStatusSummary();
    if (viewMode === "category" || viewMode === "table") {
      fetchAllCategoriesData();
    } else if (viewMode === "completed") {
      fetchCompletedOrders();
    } else if (selectedCategory) {
      loadStageViewData(selectedCategory.id, { force: true });
    }
  }, [
    viewMode,
    selectedCategory,
    fetchAllCategoriesData,
    loadStageViewData,
    fetchCompletedOrders,
  ]);

  const organizedCompletedOrders = useMemo(() => {
    if (viewMode !== "completed") return [];

    let filtered = completedOrders;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = completedOrders.filter(
        (order) =>
          order.productName?.toLowerCase().includes(lower) ||
          order.productCode?.toLowerCase().includes(lower) ||
          order.enquiryCode?.toLowerCase().includes(lower) ||
          order.purchaseOrderCode?.toLowerCase().includes(lower),
      );
    }

    return filtered;
  }, [completedOrders, searchTerm, viewMode]);

  // ADD: refreshData is recreated whenever viewMode/selectedCategory change
  // (see its own useCallback deps below). Keeping it out of the websocket
  // effect's dependency array — and reading it via a ref inside the
  // debounced handler instead — means switching views doesn't tear down
  // and rebuild the team-operations subscription (or the connection
  // itself) on every click; the effect only runs on mount and if empId
  // changes.
  const refreshDataRef = useRef(refreshData);
  useEffect(() => {
    refreshDataRef.current = refreshData;
  }, [refreshData]);

  useEffect(() => {
    const tenantId = sessionStorage.getItem("tenantId");

    if (!tenantId) {
      console.warn("Missing tenantId for WebSocket connection");
      return;
    }

    // ADD: onConnect fires immediately (synchronously) if the socket is
    // already connected — e.g. another page already opened it — and again
    // any time it (re)connects later. onDisconnect fires on close/error.
    // Drives the "Live Updates" pill in the header.
    WebSocketService.connect({
      tenantId,
      empId: user?.empId,
      onConnect: () => setIsWebSocketConnected(true),
      onDisconnect: () => setIsWebSocketConnected(false),
    });

    const debounceTimerRef = { current: null };

    const handleOperationsUpdate = (data) => {
      console.log("📨 Received team operations update:", data);
      toast.info("Operation updated in real-time", {
        position: "top-right",
        autoClose: 3000,
      });

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        refreshDataRef.current();
      }, 1000);
    };

    WebSocketService.subscribeToTeamOperationsUpdates(
      tenantId,
      handleOperationsUpdate,
    );

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      WebSocketService.unsubscribeFromTopic("operations-team");
    };
  }, [user?.empId]);

  const handleReturnStage = async (stageHistoryId, remarks = "") => {
    setProcessingAction(`return-${stageHistoryId}`);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage/${stageHistoryId}/return`,
        null,
        {
          params: { remarks },
        },
      );

      if (response.data.status === 200) {
        toast.success("Stage returned successfully!");
        setReturnPopup({ isOpen: false, stageHistoryId: null });
        setReturnRemarks("");
        refreshData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingAction(null);
    }
  };

  const openReturnPopup = (stageHistoryId) => {
    setReturnPopup({ isOpen: true, stageHistoryId });
    setReturnRemarks("");
  };

  const closeReturnPopup = () => {
    setReturnPopup({ isOpen: false, stageHistoryId: null });
    setReturnRemarks("");
  };

  const handleReturnAccept = () => {
    if (returnPopup.stageHistoryId) {
      handleReturnStage(returnPopup.stageHistoryId, returnRemarks);
    }
  };

  const handleAssignEmployee = async (stageHistoryId, employeeId) => {
    setProcessingAction(stageHistoryId);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage/${stageHistoryId}/assign/employee/${employeeId}`,
      );

      if (response.data.status === 200) {
        toast.success("Employee assigned successfully!");
        refreshData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Backfills the real checklist-history / custom-spec-history items (with
  // ids + completed status) for a single card in the "By Stage" kanban view.
  // Those cards come from the lightweight paginated endpoint, which only
  // returns task counts, so we hit the dedicated single-card endpoint
  // (`/product-order-history/employee/{operationStageHistoryId}`) and cache
  // just this one record's detail, rather than reloading the whole column.
  const fetchCardChecklistDetail = useCallback(async (historyId) => {
    setLoadingCardDetail((prev) => new Set(prev).add(historyId));
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_PROD_MANAGER}/product-order-history/employee/${historyId}`,
      );

      if (response.data.status === 200) {
        const records = response.data.data?.operationStageHistories || [];
        const fullRecord =
          records.find((r) => r.id === historyId) || records[0];

        if (fullRecord) {
          setChecklistDetailCache((prev) => ({
            ...prev,
            [historyId]: {
              operationStageCheckListHistories:
                fullRecord.operationStageCheckListHistories || [],
              customSpecificationHistories:
                fullRecord.customSpecificationHistories || [],
              productSpecificationHistories:
                fullRecord.productSpecificationHistories || [],
              minutesOfMeeting: fullRecord.minutesOfMeeting,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch checklist detail for card:", error);
      toast.error("Failed to load checklist");
    } finally {
      setLoadingCardDetail((prev) => {
        const next = new Set(prev);
        next.delete(historyId);
        return next;
      });
    }
  }, []);

  const handleCompleteTask = async (taskId, type, stageHistoryId) => {
    setProcessingAction(`${type}-${taskId}`);
    try {
      let response;

      if (type === "checklist") {
        response = await axiosInstance.put(
          `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage/checklist-history/complete`,
          {
            id: taskId,
            remarks: "",
            completedByEmployeeId: null,
          },
        );
      } else {
        response = await axiosInstance.put(
          `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage/custom-spec-history/complete`,
          {
            id: taskId,
            remarks: "",
          },
        );
      }

      if (response.data.status === 200) {
        toast.success("Task completed!");

        if (viewMode === "stage") {
          // Refresh just this card's checklist detail + the count badges,
          // instead of a full refreshData() which would reset the paginated
          // stage columns and collapse the open card.
          await fetchCardChecklistDetail(stageHistoryId);
          fetchOperationStatusSummary();
        } else {
          refreshData();
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCompleteStage = async (stageHistoryId) => {
    setProcessingAction(stageHistoryId);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage/${stageHistoryId}/complete`,
      );

      if (response.data.status === 200) {
        toast.success("Stage completed successfully!");
        refreshData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleMoveToStage = async (stageHistoryId, targetStageId) => {
    setProcessingAction(`move-${stageHistoryId}`);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_PROD_MANAGER}/operation-stage/${stageHistoryId}/move/${targetStageId}`,
      );

      if (response.data.status === 200) {
        toast.success("Moved to next stage successfully!");
        refreshData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingAction(null);
    }
  };

  const toggleCard = async (cardId, operationStageId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
      setExpandedCards(newExpanded);
      return;
    }

    newExpanded.add(cardId);
    setExpandedCards(newExpanded);
    await fetchEmployeesForStage(operationStageId);

    // Cards in the By Stage view come from the lightweight paginated
    // endpoint (task counts only). Backfill the real checklist/custom-spec
    // items the first time this card is opened — skip if we already have
    // them cached or a fetch is already in flight for this card.
    const currentRecord = (stageOperations[operationStageId] || []).find(
      (h) => h.id === cardId,
    );
    const alreadyHasRawDetail =
      currentRecord &&
      (Array.isArray(currentRecord.operationStageCheckListHistories) ||
        Array.isArray(currentRecord.customSpecificationHistories));

    if (
      currentRecord &&
      !alreadyHasRawDetail &&
      !checklistDetailCache[cardId] &&
      !loadingCardDetail.has(cardId)
    ) {
      await fetchCardChecklistDetail(cardId);
    }
  };

  const toggleTableRow = async (rowId, categoryId, stageId) => {
    const newExpanded = new Set(expandedTableRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
      await fetchDetailedCategory(categoryId);
      await fetchEmployeesForStage(stageId);
    }
    setExpandedTableRows(newExpanded);
  };

  const getAvailableStagesForItem = (categoryId, currentStageId) => {
    const detailedCategory = detailedCategoriesCache[categoryId];
    if (!detailedCategory?.operationStages) return [];

    const sortedStages = [...detailedCategory.operationStages].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );
    const currentIndex = sortedStages.findIndex((s) => s.id === currentStageId);

    const available = [];
    if (currentIndex > 0) {
      available.push(sortedStages[currentIndex - 1]);
    }
    if (currentIndex < sortedStages.length - 1) {
      available.push(sortedStages[currentIndex + 1]);
    }
    return available;
  };

  const getAvailableStages = (categoryId, currentStageId) => {
    const category = operationCategories.find((c) => c.id === categoryId);
    if (!category?.operationStages) return [];

    const sortedStages = [...category.operationStages].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );
    const currentIndex = sortedStages.findIndex((s) => s.id === currentStageId);

    const available = [];
    if (currentIndex > 0) {
      available.push(sortedStages[currentIndex - 1]);
    }
    if (currentIndex < sortedStages.length - 1) {
      available.push(sortedStages[currentIndex + 1]);
    }
    return available;
  };

  const organizedForTable = useMemo(() => {
    if (viewMode !== "table") return [];

    const tableData = [];

    operationCategories.forEach((category) => {
      const categoryOperations = allOperations[category.id] || [];

      categoryOperations.forEach((stageData) => {
        const histories = stageData.operationStageHistories || [];
        histories.forEach((history) => {
          tableData.push({
            ...history,
            categoryName: category.name,
            categoryId: category.id,
            stageName: stageData.operationStageName,
            stageId: stageData.operationStageId,
            stageSequence:
              category.operationStages?.find(
                (s) => s.id === stageData.operationStageId,
              )?.sequenceNumber || 0,
          });
        });
      });
    });

    let filtered = tableData;
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = tableData.filter(
        (item) =>
          item.productName?.toLowerCase().includes(lower) ||
          item.assignedEmployeeName?.toLowerCase().includes(lower) ||
          item.stageName?.toLowerCase().includes(lower) ||
          item.categoryName?.toLowerCase().includes(lower),
      );
    }

    filtered.sort((a, b) => {
      if (a.categoryName !== b.categoryName) {
        return a.categoryName.localeCompare(b.categoryName);
      }
      return a.stageSequence - b.stageSequence;
    });

    return filtered;
  }, [operationCategories, allOperations, searchTerm, viewMode]);

  const organizedByCategories = useMemo(() => {
    if (viewMode !== "category") return [];

    return operationCategories.map((category) => {
      const categoryOperations = allOperations[category.id] || [];

      let allHistories = [];
      categoryOperations.forEach((stageData) => {
        const histories = stageData.operationStageHistories || [];
        histories.forEach((history) => {
          allHistories.push({
            ...history,
            categoryId: category.id,
            stageName: stageData.operationStageName,
            stageId: stageData.operationStageId,
          });
        });
      });

      if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        allHistories = allHistories.filter(
          (history) =>
            history.productName?.toLowerCase().includes(lower) ||
            history.assignedEmployeeName?.toLowerCase().includes(lower) ||
            history.stageName?.toLowerCase().includes(lower),
        );
      }

      return {
        category,
        histories: allHistories,
        count: categoryCountMap[category.id] ?? allHistories.length,
      };
    });
  }, [
    operationCategories,
    allOperations,
    searchTerm,
    viewMode,
    categoryCountMap,
  ]);

  const organizedByStages = useMemo(() => {
    if (viewMode !== "stage" || !selectedCategory) return [];

    const detailedCategory = detailedCategoriesCache[selectedCategory.id];
    if (!detailedCategory?.operationStages) return [];

    const sortedStages = [...detailedCategory.operationStages].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );

    return sortedStages.map((stage) => {
      let histories = stageOperations[stage.id] || [];

      if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        histories = histories.filter(
          (history) =>
            history.productName?.toLowerCase().includes(lower) ||
            history.assignedEmployeeName?.toLowerCase().includes(lower),
        );
      }

      return {
        stage,
        histories: histories.map((h) => ({
          ...h,
          ...(checklistDetailCache[h.id] || {}),
          categoryId: selectedCategory.id,
          stageId: stage.id,
        })),
        count: stageCountMap[stage.id] ?? histories.length,
      };
    });
  }, [
    selectedCategory,
    detailedCategoriesCache,
    stageOperations,
    searchTerm,
    viewMode,
    stageCountMap,
    checklistDetailCache,
  ]);

  useEffect(() => {
    if (viewMode !== "stage") return;

    const stageIds = new Set();

    organizedByStages.forEach((stageGroup) => {
      stageIds.add(stageGroup.stage.id);
    });

    stageIds.forEach((stageId) => {
      if (!employeesCache[stageId]) {
        fetchEmployeesForStage(stageId);
      }
    });
  }, [organizedByStages, employeesCache, fetchEmployeesForStage, viewMode]);

  useEffect(() => {
    if (viewMode !== "category") return;

    organizedByCategories.forEach((cat) => {
      cat.histories.forEach((history) => {
        if (!employeesCache[history.stageId]) {
          fetchEmployeesForStage(history.stageId);
        }
      });
    });
  }, [organizedByCategories, employeesCache, fetchEmployeesForStage, viewMode]);

  const getStatusBadge = (status) => {
    const configs = {
      PENDING: { label: "Pending", color: "orange", icon: Clock },
      PROCESSING: { label: "In Progress", color: "blue", icon: Users },
      ASSIGNED: { label: "In Progress", color: "blue", icon: Users },
      COMPLETED: { label: "Completed", color: "green", icon: CheckCircle },
      RETURNED: { label: "Returned", color: "red", icon: XCircle },
    };

    const config = configs[status] || configs.PENDING;
    const Icon = config.icon;

    return (
      <span className={`status-badge status-${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const handleDragStart = (e, history, stageId) => {
    setDraggedItem({ ...history, currentStageId: stageId });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // Fires on scroll of a stage column's card list. Once the user is within
  // ~150px of the bottom, and that stage actually has more pages and isn't
  // already fetching one, request the next batch of 10 for just this stage.
  const handleColumnScroll = useCallback(
    (stageId) => (e) => {
      const el = e.currentTarget;
      columnScrollPositionRef.current[stageId] = el.scrollTop;

      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
      if (!nearBottom) return;

      const stagePagination = paginationByStage[stageId];
      if (!stagePagination?.hasMore) return;
      if (loadingStageIdsRef.current.has(stageId)) return;

      fetchStageHistoryPage(stageId);
    },
    [paginationByStage, fetchStageHistoryPage],
  );

  // Runs after every render, before paint. Re-applies each column's last
  // known scrollTop so state updates (expanding a card, the checklist
  // backfill landing, a WebSocket refresh, etc.) don't reset the column back
  // to the top — the browser only remembers scroll position on its own as
  // long as the DOM node persists, and we don't want to depend on that.
  useLayoutEffect(() => {
    Object.entries(columnNodesRef.current).forEach(([stageId, el]) => {
      if (!el) return;
      const savedPosition = columnScrollPositionRef.current[stageId];
      if (savedPosition != null && el.scrollTop !== savedPosition) {
        el.scrollTop = savedPosition;
      }
    });
  });

  const openMOM = (history) => {
    setMomModal({
      isOpen: true,
      enquiryId: history.enquiryId || history.id,
      enquiryStageHistoryId: history.id,
      productName: history.productName,
      readOnly: false,
    });
  };

  const closeMOM = () => {
    setMomModal({
      isOpen: false,
      enquiryId: null,
      enquiryStageHistoryId: null,
      productName: "",
      readOnly: false,
    });
  };

  const openBOM = (history) => {
    console.log("Opening BOM with history:", history); // Debug log

    // Make sure we have the required productOrderHistoryId
    if (!history.productOrderHistory) {
      toast.error("Missing product order history ID. Cannot open BOM.");
      console.error("Missing productOrderHistoryId in history:", history);
      return;
    }

    const enquiryData = {
      enquiryId: history.enquiryId || history.id,
      productOrderHistoryId: history.productOrderHistory,
      productHistories: [
        {
          productId: history.productId,
          product: {
            name: history.productName || "Unknown Product",
          },
        },
      ],
      leadName: null,
      clientName: history.clientCode || "Client",
      stageName: history.stageName || "Operation Stage",
    };

    console.log("BOM enquiryData:", enquiryData); // Debug log

    setBomModal({
      isOpen: true,
      enquiryData,
    });
  };

  const closeBOM = () => {
    setBomModal({
      isOpen: false,
      stageHistoryId: null,
      productId: null,
      productName: "",
      readOnly: false,
    });
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();

    if (!draggedItem) {
      return;
    }

    if (draggedItem.currentStageId === targetStageId) {
      setDraggedItem(null);
      return;
    }

    const availableStages = getAvailableStages(
      draggedItem.categoryId,
      draggedItem.currentStageId,
    );
    const canMove = availableStages.some((s) => s.id === targetStageId);

    if (!canMove) {
      toast.error("Can only move to adjacent stages (previous or next)");
      setDraggedItem(null);
      return;
    }

    if (draggedItem.operationStatus === "COMPLETED") {
      await handleMoveToStage(draggedItem.id, targetStageId);
    } else {
      toast.error("Only completed operations can be moved between stages");
    }

    setDraggedItem(null);
  };

  const CompletedOrderCard = ({ order }) => {
    const isExpanded = expandedCards.has(order.id);
    const [expandedStages, setExpandedStages] = useState(new Set());

    const toggleStage = (stageId) => {
      const newExpanded = new Set(expandedStages);
      if (newExpanded.has(stageId)) {
        newExpanded.delete(stageId);
      } else {
        newExpanded.add(stageId);
      }
      setExpandedStages(newExpanded);
    };

    return (
      <div className="completed-order-card">
        <div
          className="card-main"
          onClick={() => {
            const newExpanded = new Set(expandedCards);
            if (newExpanded.has(order.id)) {
              newExpanded.delete(order.id);
            } else {
              newExpanded.add(order.id);
            }
            setExpandedCards(newExpanded);
          }}
        >
          <div className="card-left">
            <div className="product-header">
              <h3 className="product-name">{order.productName}</h3>
              <span className="product-code">{order.productCode}</span>
            </div>
            <div className="order-meta">
              <span className="meta-item">
                <Package size={14} /> {order.quantity} units
              </span>
              {order.enquiryCode && (
                <span className="meta-item">Enquiry: {order.enquiryCode}</span>
              )}
              {order.purchaseOrderCode && (
                <span className="meta-item">PO: {order.purchaseOrderCode}</span>
              )}
            </div>
            {order.remarks && <p className="order-remarks">{order.remarks}</p>}
          </div>
          <div className="card-right">
            <span className="status-badge status-completed">
              <CheckCircle size={14} />
              Completed
            </span>
            <button className="expand-icon">
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="card-expanded" onClick={(e) => e.stopPropagation()}>
            {order.specificationHistories?.length > 0 && (
              <div className="specs-section">
                <label className="section-label">Specifications</label>
                <div className="specs-grid">
                  {order.specificationHistories.map((spec) => (
                    <div key={spec.id} className="spec-item">
                      <span className="spec-name">
                        {spec.specificationName}
                      </span>
                      <span className="spec-value">
                        {spec.specificationValue}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {order.operationStages?.length > 0 && (
              <div className="stages-section">
                <label className="section-label">Operation Stages</label>
                <div className="stages-list">
                  {order.operationStages
                    .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                    .map((stage) => (
                      <div key={stage.id} className="stage-item">
                        <div
                          className="stage-header"
                          onClick={() => toggleStage(stage.id)}
                        >
                          <div className="stage-info">
                            <span className="stage-name">{stage.name}</span>
                            {stage.description && (
                              <span className="stage-desc">
                                {stage.description}
                              </span>
                            )}
                          </div>
                          <div className="stage-badges">
                            {stage.isInitialStage && (
                              <span className="badge badge-initial">
                                Initial
                              </span>
                            )}
                            {stage.isFinalStage && (
                              <span className="badge badge-final">Final</span>
                            )}
                            <button className="expand-icon-small">
                              {expandedStages.has(stage.id) ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </div>
                        </div>

                        {expandedStages.has(stage.id) &&
                          stage.checkLists?.length > 0 && (
                            <div className="checklist-section">
                              <label className="checklist-label">
                                Checklist Items
                              </label>
                              <div className="checklist-items">
                                {stage.checkLists
                                  .sort(
                                    (a, b) =>
                                      a.sequenceNumber - b.sequenceNumber,
                                  )
                                  .map((item) => (
                                    <div
                                      key={item.id}
                                      className="checklist-item"
                                    >
                                      <CheckCircle
                                        size={14}
                                        className="check-icon"
                                      />
                                      <div className="item-details">
                                        <span className="item-name">
                                          {item.itemName}
                                        </span>
                                        {item.description && (
                                          <span className="item-desc">
                                            {item.description}
                                          </span>
                                        )}
                                        {item.isMandatory && (
                                          <span className="mandatory-badge">
                                            Mandatory
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const OperationCard = ({
    history,
    operationStageId,
    employees,
    showStageName = false,
    showMoveOptions = false,
  }) => {
    const isExpanded = expandedCards.has(history.id);
    const canAssign =
      (history.operationStatus === "PENDING" ||
        history.operationStatus === "PROCESSING") &&
      !history.assignedEmployeeId;
    const canComplete =
      (history.operationStatus === "ASSIGNED" ||
        history.operationStatus === "PROCESSING") &&
      !!history.assignedEmployeeId;
    const isCompleted = history.operationStatus === "COMPLETED";
    const isReturned = history.operationStatus === "RETURNED";

    const allTasks = [
      ...(history.operationStageCheckListHistories || []).map((item) => ({
        ...item,
        type: "checklist",
        name: item.taskName,
      })),
      ...(history.customSpecificationHistories || []).map((item) => ({
        ...item,
        type: "custom",
        name: item.specs,
      })),
    ];

    // The paginated `/operation-categories/team/operation-stage-histories`
    // endpoint used for the By Stage kanban only sends task counts, not the
    // individual checklist/spec objects (those still come from the bulk
    // endpoint used by the By Category and Table views). Fall back to the
    // counts whenever the detailed arrays aren't present.
    const hasDetailedTasks =
      Array.isArray(history.operationStageCheckListHistories) ||
      Array.isArray(history.customSpecificationHistories);
    const completedTasks = hasDetailedTasks
      ? allTasks.filter((t) => t.completed).length
      : history.completedTasksCount || 0;
    const totalTasks = hasDetailedTasks
      ? allTasks.length
      : history.totalTasksCount || 0;
    const allTasksComplete = totalTasks > 0 && completedTasks === totalTasks;

    const availableStages =
      showMoveOptions && isCompleted
        ? getAvailableStages(history.categoryId, operationStageId)
        : [];

    return (
      <div
        className={`prod-card status-${history.operationStatus.toLowerCase()}`}
        draggable={viewMode === "stage"}
        onDragStart={(e) =>
          viewMode === "stage" && handleDragStart(e, history, operationStageId)
        }
      >
        <div
          className="card-main"
          onClick={() => toggleCard(history.id, operationStageId)}
        >
          <div className="card-left">
            <div className="product-header">
              <h3 className="product-name">{history.productName}</h3>

              {showStageName && history.stageName && (
                <span className="stage-badge-inline">
                  [{history.stageName}]
                </span>
              )}
            </div>

            {/* BUSINESS INFO */}
            <div className="product-meta-modern">
              {history.clientName && <span>Client: {history.clientName}</span>}
            </div>

            {isReturned && history.returnedMessage && (
              <div className="returned-message-hint">
                <XCircle size={12} />
                <span>{history.returnedMessage}</span>
              </div>
            )}

            {/* OPERATION INFO */}
            <div className="card-meta">
              <span className="qty">
                <Package size={16} />
                {history.quantity} Units
              </span>

              {history.assignedEmployeeName && (
                <span className="employee">
                  <Users size={16} />
                  {history.assignedEmployeeName}
                </span>
              )}

              {history.purchaseOrderCode && (
                <span>PO: {history.purchaseOrderCode}</span>
              )}

              {history.enquiryCode && (
                <span>Enquiry: {history.enquiryCode}</span>
              )}

              {history.productCode && <span>Code: {history.productCode}</span>}
            </div>

            {totalTasks > 0 && (
              <div className="task-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(completedTasks / totalTasks) * 100}%`,
                    }}
                  />
                </div>
                <span className="progress-text">
                  {completedTasks}/{totalTasks} tasks
                </span>
              </div>
            )}
          </div>

          <div className="card-right">
            {/* Show STATUS if employee assigned, else show ASSIGN dropdown */}
            {history.assignedEmployeeId
              ? getStatusBadge(history.operationStatus)
              : canAssign &&
                employees &&
                employees.length > 0 && (
                  <select
                    className="employee-select-inline"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignEmployee(
                          history.id,
                          parseInt(e.target.value),
                        );
                      }
                    }}
                    disabled={processingAction === history.id}
                    value=""
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">Assign Employee...</option>
                    {employees.map((emp) => (
                      <option key={emp.empId} value={emp.empId}>
                        {emp.firstName} {emp.lastName}
                      </option>
                    ))}
                  </select>
                )}

            <div className="card-actions-inline">
              <button
                className="edit-btn-modern mom-btn"
                title="Minutes of Meeting"
                onClick={(e) => {
                  e.stopPropagation();
                  openMOM(history);
                }}
              >
                <Speech size={13} />
                <span className="action-label">MOM</span>
              </button>

              <button
                className="edit-btn-modern bom-btn"
                title="Bill of Materials"
                onClick={(e) => {
                  e.stopPropagation();
                  openBOM(history);
                }}
              >
                <FileText size={13} />
                <span className="action-label">BOM</span>
              </button>
            </div>

            <button className="expand-icon">
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="card-expanded" onClick={(e) => e.stopPropagation()}>
            {showMoveOptions && isCompleted && availableStages.length > 0 && (
              <div className="move-stage-section">
                <label className="section-label">Move to Stage</label>
                <select
                  className="stage-select"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleMoveToStage(history.id, parseInt(e.target.value));
                    }
                  }}
                  disabled={processingAction === `move-${history.id}`}
                  value=""
                >
                  <option value="">Select stage to move...</option>
                  {availableStages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!canAssign &&
              history.assignedEmployeeId &&
              history.assignedEmployeeName && (
                <div className="assigned-employee-section">
                  <label className="section-label">Assigned Employee</label>
                  <div className="employee-info">
                    <span>{history.assignedEmployeeName}</span>
                  </div>
                </div>
              )}

            {history.productSpecificationHistories?.length > 0 && (
              <div className="specs-section">
                <label className="section-label">Specifications</label>
                <div className="specs-grid">
                  {history.productSpecificationHistories.map((spec) => (
                    <div key={spec.id} className="spec-item">
                      <span className="spec-name">
                        {spec.specificationName}
                      </span>
                      <span className="spec-value">
                        {spec.specificationValue} {spec.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {totalTasks > 0 && !hasDetailedTasks && (
              <div className="tasks-section tasks-loading">
                <label className="section-label">Tasks</label>
                <p
                  className="loading-text"
                  style={{ fontSize: "13px", color: "#888", margin: 0 }}
                >
                  {loadingCardDetail.has(history.id)
                    ? "Loading checklist..."
                    : "Checklist unavailable"}
                </p>
              </div>
            )}

            {allTasks.length > 0 && (
              <div className="tasks-section">
                <label className="section-label">Tasks</label>
                <div className="tasks-list">
                  {allTasks.map((task) => (
                    <div
                      key={`${task.type}-${task.id}`}
                      className={`task-item ${
                        task.completed ? "completed" : ""
                      } ${task.type === "custom" ? "task-custom" : ""}`}
                    >
                      <div className="task-left">
                        {task.completed ? (
                          <CheckCircle size={16} className="task-check" />
                        ) : (
                          <div
                            className={`task-checkbox ${
                              canComplete ? "clickable" : "disabled"
                            }`}
                            onClick={() =>
                              canComplete
                                ? handleCompleteTask(
                                    task.id,
                                    task.type,
                                    history.id,
                                  )
                                : toast.error("Assign an employee first")
                            }
                            style={
                              canComplete
                                ? { cursor: "pointer" }
                                : { cursor: "not-allowed" }
                            }
                            title={
                              !history.assignedEmployeeId
                                ? "Assign an employee to enable this task"
                                : undefined
                            }
                          />
                        )}
                        <div className="task-name-wrapper">
                          <span className="task-name">{task.name}</span>
                          {task.type === "custom" && (
                            <span className="custom-badge">Custom Spec</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isReturned && (history.returnedMessage || history.remarks) && (
              <div className="remarks-section">
                <label className="section-label">Return Remarks</label>
                <div className="remarks-content">
                  {history.returnedMessage || history.remarks}
                </div>
              </div>
            )}

            <div className="action-buttons-row">
              {history.assignedEmployeeId && !isReturned && (
                <button
                  className="return-stage-btn"
                  onClick={() => openReturnPopup(history.id)}
                  disabled={processingAction === `return-${history.id}`}
                >
                  <XCircle size={16} />
                  Return Stage
                </button>
              )}
              {(canComplete || isReturned) &&
                history.assignedEmployeeId &&
                (allTasksComplete || totalTasks === 0) && (
                  <button
                    className="complete-stage-btn"
                    onClick={() => handleCompleteStage(history.id)}
                    disabled={processingAction === history.id}
                  >
                    <CheckCircle size={16} />
                    Complete Stage
                  </button>
                )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getViewSubtitle = () => {
    switch (viewMode) {
      case "category":
        return "Overview by operation categories";
      case "stage":
        return "Track operations through stages";
      case "table":
        return "Detailed table view of all operations";
      case "completed":
        return "View completed product orders";
      default:
        return "";
    }
  };
  // Function to get API endpoint for MOM
  const getMomApiEndpoint = (endpoint) => {
    return `${process.env.REACT_APP_SALES}${endpoint}`;
  };

  if (loading && operationCategories.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="prod-manager">
      <div className="header">
        <div className="header-left">
          <h1>Production Manager</h1>
          <p className="subtitle">{getViewSubtitle()}</p>
        </div>
        <div className="header-right">
          <div className="view-toggles">
            <button
              onClick={() => setViewMode("stage")}
              className={`view-toggle ${viewMode === "stage" ? "active" : ""}`}
              title="Stage View"
            >
              <Columns size={18} />
              <span>By Stage</span>
            </button>
            <button
              onClick={() => setViewMode("category")}
              className={`view-toggle ${
                viewMode === "category" ? "active" : ""
              }`}
              title="Category View"
            >
              <LayoutGrid size={18} />
              <span>By Category</span>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`view-toggle ${viewMode === "table" ? "active" : ""}`}
              title="Table View"
            >
              <TableIcon size={18} />
              <span>Table</span>
            </button>
            <button
              onClick={() => setViewMode("completed")}
              className={`view-toggle ${
                viewMode === "completed" ? "active" : ""
              }`}
              title="Completed Orders"
            >
              <CheckCircle size={18} />
              <span>Completed</span>
            </button>
          </div>
          {isWebSocketConnected ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                borderRadius: 999,
                background: "#dcfce7",
                color: "#15803d",
                border: "1px solid #86efac",
                fontSize: "0.85rem",
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              <Icon icon="mdi:access-point" />
              <span>Live Updates</span>
            </div>
          ) : (
            <ButtonWithIcon
              icon="mdi:refresh"
              text="Refresh"
              onClick={refreshData}
              disabled={loading}
              variant="secondary"
            />
          )}
        </div>
      </div>

      {viewMode === "stage" && (
        <div className="category-tabs">
          {operationCategories.map((category) => (
            <button
              key={category.id}
              className={`category-tab ${
                selectedCategory?.id === category.id ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category.name}
            </button>
          ))}
        </div>
      )}

      <div className="search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search by product, employee, or stage..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div
        className={viewMode === "table" ? "table-container" : "kanban-board"}
      >
        {loading ? (
          <div>
            <LoadingScreen />
          </div>
        ) : viewMode === "table" ? (
          <div className="table-wrapper">
            <table className="operations-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Stage</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizedForTable.map((item) => {
                  const allTasks = [
                    ...(item.operationStageCheckListHistories || []),
                    ...(item.customSpecificationHistories || []),
                  ];
                  const completedTasks = allTasks.filter(
                    (t) => t.completed,
                  ).length;
                  const totalTasks = allTasks.length;
                  const isCompleted = item.operationStatus === "COMPLETED";
                  const isReturned = item.operationStatus === "RETURNED";
                  const isExpanded = expandedTableRows.has(item.id);
                  const availableStages = isCompleted
                    ? getAvailableStagesForItem(item.categoryId, item.stageId)
                    : [];

                  return (
                    <React.Fragment key={item.id}>
                      <tr
                        className={`status-${item.operationStatus.toLowerCase()}`}
                      >
                        <td className="category-cell">{item.categoryName}</td>
                        <td className="stage-cell">{item.stageName}</td>
                        <td className="product-cell">
                          <strong>{item.productName}</strong>
                        </td>
                        <td className="qty-cell">{item.quantity} units</td>
                        <td className="employee-cell">
                          {item.assignedEmployeeName || "-"}
                        </td>
                        <td className="status-cell">
                          {getStatusBadge(item.operationStatus)}
                        </td>
                        <td className="progress-cell">
                          {totalTasks > 0 ? (
                            <div className="table-progress">
                              <div className="progress-bar">
                                <div
                                  className="progress-fill"
                                  style={{
                                    width: `${
                                      (completedTasks / totalTasks) * 100
                                    }%`,
                                  }}
                                />
                              </div>
                              <span className="progress-text">
                                {completedTasks}/{totalTasks}
                              </span>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="actions-cell">
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            {isCompleted && availableStages.length > 0 ? (
                              <select
                                className="table-stage-select"
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleMoveToStage(
                                      item.id,
                                      parseInt(e.target.value),
                                    );
                                  }
                                }}
                                disabled={
                                  processingAction === `move-${item.id}`
                                }
                                value=""
                              >
                                <option value="">Move to...</option>
                                {availableStages.map((stage) => (
                                  <option key={stage.id} value={stage.id}>
                                    {stage.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <button
                                className="view-details-btn"
                                onClick={() =>
                                  toggleTableRow(
                                    item.id,
                                    item.categoryId,
                                    item.stageId,
                                  )
                                }
                              >
                                {isExpanded ? "Hide Details" : "View Details"}
                              </button>
                            )}
                            <button
                              className="icon-btn-table"
                              title="Minutes of Meeting"
                              onClick={() => openMOM(item)}
                            >
                              <FileText size={14} />
                            </button>
                            <button
                              className="icon-btn-table"
                              title="Bill of Materials"
                              onClick={() => openBOM(item)}
                            >
                              <ClipboardList size={14} />
                            </button>
                            {!isReturned && (
                              <button
                                className="return-stage-btn-table"
                                onClick={() => openReturnPopup(item.id)}
                                disabled={
                                  processingAction === `return-${item.id}`
                                }
                                title="Return Stage"
                              >
                                <XCircle size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="expanded-row">
                          <td colSpan={8}>
                            <div className="table-expanded-content">
                              <button
                                className="close-expanded-btn"
                                onClick={() =>
                                  toggleTableRow(
                                    item.id,
                                    item.categoryId,
                                    item.stageId,
                                  )
                                }
                              >
                                <X size={16} />
                              </button>

                              {(item.operationStatus === "PENDING" ||
                                item.operationStatus === "PROCESSING") &&
                                !item.assignedEmployeeId &&
                                employeesCache[item.stageId]?.length > 0 && (
                                  <div className="expanded-section">
                                    <label className="section-label">
                                      Assign Employee
                                    </label>
                                    <div className="employee-grid">
                                      {employeesCache[item.stageId].map(
                                        (emp) => (
                                          <button
                                            key={emp.empId}
                                            className="employee-chip"
                                            onClick={() =>
                                              handleAssignEmployee(
                                                item.id,
                                                emp.empId,
                                              )
                                            }
                                            disabled={
                                              processingAction === item.id
                                            }
                                          >
                                            <Users size={14} />
                                            {emp.firstName} {emp.lastName}
                                          </button>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}

                              {item.assignedEmployeeId &&
                                item.assignedEmployeeName && (
                                  <div className="expanded-section">
                                    <label className="section-label">
                                      Assigned Employee
                                    </label>
                                    <div className="employee-info">
                                      <Users size={16} />
                                      <span>{item.assignedEmployeeName}</span>
                                    </div>
                                  </div>
                                )}

                              {isReturned &&
                                (item.returnedMessage || item.remarks) && (
                                  <div className="expanded-section">
                                    <label className="section-label">
                                      Return Remarks
                                    </label>
                                    <div className="remarks-content">
                                      {item.returnedMessage || item.remarks}
                                    </div>
                                  </div>
                                )}

                              {item.productSpecificationHistories?.length >
                                0 && (
                                <div className="expanded-section">
                                  <label className="section-label">
                                    Specifications
                                  </label>
                                  <div className="specs-grid">
                                    {item.productSpecificationHistories.map(
                                      (spec) => (
                                        <div
                                          key={spec.id}
                                          className="spec-item"
                                        >
                                          <span className="spec-name">
                                            {spec.specificationName}
                                          </span>
                                          <span className="spec-value">
                                            {spec.specificationValue}{" "}
                                            {spec.unit}
                                          </span>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}

                              {allTasks.length > 0 && (
                                <div className="expanded-section">
                                  <label className="section-label">Tasks</label>
                                  <div className="tasks-list">
                                    {[
                                      ...(
                                        item.operationStageCheckListHistories ||
                                        []
                                      ).map((task) => ({
                                        ...task,
                                        type: "checklist",
                                        name: task.taskName,
                                      })),
                                      ...(
                                        item.customSpecificationHistories || []
                                      ).map((task) => ({
                                        ...task,
                                        type: "custom",
                                        name: task.specs,
                                      })),
                                    ].map((task) => {
                                      const canComplete =
                                        (item.operationStatus === "ASSIGNED" ||
                                          item.operationStatus ===
                                            "PROCESSING") &&
                                        !!item.assignedEmployeeId;
                                      return (
                                        <div
                                          key={`${task.type}-${task.id}`}
                                          className={`task-item ${
                                            task.completed ? "completed" : ""
                                          } ${
                                            task.type === "custom"
                                              ? "task-custom"
                                              : ""
                                          }`}
                                        >
                                          <div className="task-left">
                                            {task.completed ? (
                                              <CheckCircle
                                                size={16}
                                                className="task-check"
                                              />
                                            ) : (
                                              <div
                                                className={`task-checkbox ${
                                                  canComplete && !task.completed
                                                    ? "clickable"
                                                    : "disabled"
                                                }`}
                                                onClick={() =>
                                                  canComplete && !task.completed
                                                    ? handleCompleteTask(
                                                        task.id,
                                                        task.type,
                                                        item.id,
                                                      )
                                                    : toast.error(
                                                        "Assign an employee first",
                                                      )
                                                }
                                                style={
                                                  canComplete && !task.completed
                                                    ? { cursor: "pointer" }
                                                    : { cursor: "not-allowed" }
                                                }
                                                title={
                                                  !item.assignedEmployeeId
                                                    ? "Assign an employee to enable this task"
                                                    : undefined
                                                }
                                              />
                                            )}
                                            <div className="task-name-wrapper">
                                              <span className="task-name">
                                                {task.name}
                                              </span>
                                              {task.type === "custom" && (
                                                <span className="custom-badge">
                                                  Custom Spec
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {item.assignedEmployeeId &&
                                (item.operationStatus === "ASSIGNED" ||
                                  item.operationStatus === "PROCESSING" ||
                                  isReturned) &&
                                (totalTasks === 0 ||
                                  completedTasks === totalTasks) && (
                                  <button
                                    className="complete-stage-btn"
                                    onClick={() => handleCompleteStage(item.id)}
                                    disabled={processingAction === item.id}
                                  >
                                    <CheckCircle size={16} />
                                    Complete Stage
                                  </button>
                                )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {organizedForTable.length === 0 && (
              <div className="empty-table">
                <AlertCircle size={24} />
                <p>No operations found</p>
              </div>
            )}
          </div>
        ) : viewMode === "completed" ? (
          <div className="completed-orders-view">
            {organizedCompletedOrders.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={48} />
                <h3>No Completed Orders</h3>
                <p>Completed orders will appear here</p>
              </div>
            ) : (
              <div className="completed-orders-grid">
                {organizedCompletedOrders.map((order) => (
                  <CompletedOrderCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </div>
        ) : viewMode === "category" ? (
          <div className="kanban-columns">
            {organizedByCategories.map(({ category, histories, count }) => (
              <div key={category.id} className="kanban-column">
                <div className="column-header">
                  <div className="column-title">
                    <h2>{category.name}</h2>
                  </div>
                  <span className="column-count">{count}</span>
                </div>

                <div className="column-content">
                  {histories.length === 0 ? (
                    <div className="empty-column">
                      <AlertCircle size={20} />
                      <p>No operations</p>
                    </div>
                  ) : (
                    histories.map((history) => (
                      <OperationCard
                        key={history.id}
                        history={history}
                        operationStageId={history.stageId}
                        employees={employeesCache[history.stageId]}
                        showStageName={true}
                        showMoveOptions={true}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="kanban-columns">
            {organizedByStages.map(({ stage, histories, count }) => (
              <div
                key={stage.id}
                className="kanban-column"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                <div className="column-header">
                  <div className="column-title">
                    <h2>{stage.name}</h2>
                    {stage.description && (
                      <p className="column-desc">{stage.description}</p>
                    )}
                  </div>
                  <span className="column-count">{count}</span>
                </div>

                <div
                  className="column-content"
                  ref={(el) => {
                    columnNodesRef.current[stage.id] = el;
                  }}
                  onScroll={handleColumnScroll(stage.id)}
                >
                  {histories.length === 0 ? (
                    <div className="empty-column">
                      <AlertCircle size={20} />
                      <p>No operations</p>
                    </div>
                  ) : (
                    histories.map((history) => (
                      <OperationCard
                        key={history.id}
                        history={history}
                        operationStageId={stage.id}
                        employees={employeesCache[stage.id]}
                        showStageName={false}
                        showMoveOptions={false}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MOM Modal */}
      {momModal.isOpen && (
        <div className="modal-overlay" onClick={closeMOM}>
          <div onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <MinutesOfMeetingManager
                enquiryId={momModal.enquiryId}
                enquiryStageHistoryId={momModal.enquiryStageHistoryId}
                axiosInstance={axiosInstance}
                getApiEndpoint={getMomApiEndpoint}
                onClose={closeMOM}
                readOnly={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* BOM Modal */}
      {bomModal.isOpen && (
        <div className="modal-overlay" onClick={closeBOM}>
          <div onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <EnquiryBOMModal
                isOpen={bomModal.isOpen}
                enquiryData={bomModal.enquiryData}
                axiosInstance={axiosInstance}
                onClose={() =>
                  setBomModal({ isOpen: false, enquiryData: null })
                }
              />
            </div>
          </div>
        </div>
      )}

      <WarningPopup
        isOpen={returnPopup.isOpen}
        onClose={closeReturnPopup}
        onAccept={handleReturnAccept}
        onDecline={closeReturnPopup}
        title="Return Stage"
        message={
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
              Are you sure you want to return this stage? This action will mark
              the stage as returned.
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                textAlign: "left",
              }}
            >
              <label
                style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}
              >
                Remarks (Optional):
              </label>
              <textarea
                value={returnRemarks}
                onChange={(e) => setReturnRemarks(e.target.value)}
                placeholder="Enter remarks for returning this stage..."
                rows={4}
                style={{
                  padding: "10px 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  outline: "none",
                  minHeight: "80px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        }
        acceptText="Return Stage"
        declineText="Cancel"
      />
    </div>
  );
};

export default ProdManager;
